import { isStringTypesafe } from '@/helpers/typeValidations'
import {
  addUserToAttendingList,
  removeUserFromAttendingList,
} from '@/service/v3/endpoints/events'
import { ref, unref } from 'vue'
import type { MaybeRef } from '../usePolicyTypeSelection'
import { useOnError } from './helpers/useOnError'
import { useQueryClient } from '@tanstack/vue-query'
import { QUERY_KEY_ILT_REGISTRATIONS } from '@/queries/iltRegistrations/iltRegistrationKeys.ts'
import { QUERY_KEY_EVENT } from '@/queries/events/eventKeys.ts'

interface DateUserHandlerInput {
  selectedDateId: MaybeRef<number | undefined>
}

export const useDateUserHandler = ({
  selectedDateId,
}: DateUserHandlerInput) => {
  const popUpError = ref<{ error: string } | undefined>(undefined)
  useOnError(popUpError)
  const graphqlClient = useQueryClient()

  const typeSafeNumericalId = (
    id: string | number | undefined
  ): number | undefined => {
    if (!id) return undefined
    return isStringTypesafe(id) ? parseInt(id) : id
  }

  const addUserToDate = async (userId: number) => {
    try {
      await addUserToAttendingList(unref(selectedDateId), userId)

      await graphqlClient.invalidateQueries({
        queryKey: [QUERY_KEY_ILT_REGISTRATIONS],
      })
      await graphqlClient.invalidateQueries({
        queryKey: [QUERY_KEY_EVENT],
      })
    } catch (error) {
      popUpError.value = { error: JSON.stringify(error) }
    }
  }

  const removeUserFromDate = async (
    userId: number,
    dateId: number | string | undefined
  ) => {
    try {
      await removeUserFromAttendingList(
        typeSafeNumericalId(unref(dateId))!,
        userId
      )
      await graphqlClient.invalidateQueries({
        queryKey: [QUERY_KEY_ILT_REGISTRATIONS],
      })
      await graphqlClient.invalidateQueries({
        queryKey: [QUERY_KEY_EVENT],
      })
    } catch (error) {
      popUpError.value = { error: JSON.stringify(error) }
    }
  }

  return { addUserToDate, removeUserFromDate }
}
